import { JourneyApiColumns } from '../constants/journey-api-columns'
import { NotificationApiColumns } from '../constants/notification-api-columns'
import { PromptApiColumns } from '../constants/prompt-api-columns'
import { PubNetworkApiColumns } from '../constants/pub-network-api-columns'
import { NotificationApiOrderBlacklist } from '../constants/notification-api-order'
import { IReportConfig } from '../interfaces/report-config'
import { COLUMN_DROPDOWN_OPTIONS } from '../constants/column-dropdown-options'
import { COLUMN_TABLE_ORDER } from '../constants/column-table-order'
import { TIME_RANGE_OPTIONS } from '../constants/time-range-options'
import { SegmentApiColumns } from '../constants/segment-api-columns'
import { MonthlyUniquesApiColumns } from '../constants/monthly-uniques-api-columns'
import { ColumnDropdownOption } from '../types'
import { AppMessageApiColumns } from '../constants/app-message-api-columns'
import { LiveActivityApiColumns } from '../constants/live-activity-api-columns'
import { COLUMN_CONFIGS_BY_TYPE_MAP } from '../constants/base'
import { ReportConfigurationBreakdown } from '../enums/report-configuration-breakdown'
import { ReportConfigurationLevel } from '../enums/report-configuration-level'
import { ReportConfigurationType } from '../enums/report-configuration-type'
import { ReportDatePreset } from '../enums/report-date-preset'
import { ReportDateIncrement } from '../enums/report-date-increment'
import { ReportEntity } from '../enums/report-entity'
import { ReportFilterOperator } from '../enums/report-filter-operator'

type DateRange = {
    since: string
    through: string
}

export type ReportFilter = {
    field: string
    operator: Lowercase<keyof typeof ReportFilterOperator>
    value: any
    forceAllow?: boolean
    builderRestriction?: string
    isEnum?: boolean
}

type ReportPagination = {
    page: number
    limit: number
}

type ReportOrder = {
    field: string
    direction: 'asc' | 'desc'
    nulls?: 'first' | 'last'
}

export type InsightsReportContract = {
    organization_id?: number
    domain_ids?: number[]
    entity_ids?: number[]
    entity: ReportEntity
    /**
     * breakdowns can be a computed string value from the ReportConfigurationBreakdown enum
     * i.e.
     *      BROWSER,PLACEMENT,OPERATING_SYSTEM = 'BROWSER,PLACEMENT,OPERATING_SYSTEM'
     *      becomes:
     *      breakdowns: ['browser', 'placement', 'operating_system']
     */
    breakdowns: string[] // (keyof typeof ReportConfigurationLevel & keyof typeof ReportConfigurationBreakdown)[]
    fields: string[]
    date_range?: DateRange
    date_preset?: ReportDatePreset
    date_increment: ReportDateIncrement
    filters: ReportFilter[]
    pagination?: ReportPagination
    order?: ReportOrder
    addtRequiredFields?: string[]
    action_attribution?: 'event_time' | 'send_time'

    // download options
    filename?: string
    format?: string
}

export type ReportContractOptions = {
    organization_id?: number
    domain_ids?: number[]
    downloadable?: boolean
}

export function buildApiReportContract(
    reportConfig: IReportConfig,
    type: ReportConfigurationType,
    level: ReportConfigurationLevel,
    options: ReportContractOptions = {},
): InsightsReportContract {
    const globalTable = (window as any).__SW_REPORT_TABLE__ || {}

    const contract: InsightsReportContract = {
        date_increment: reportConfig.dateIncrement,
        order: {
            field: 'send_date',
            direction: globalTable.sortDirection || 'desc',
        },
        breakdowns: [],
        fields: [],
        filters: [...(reportConfig.filters || []).filter((f: any) => !!f)],
        entity: ReportEntity.NOTIFICATIONS,
    }

    const downloadable = options.downloadable || false

    if (options.organization_id) {
        contract.organization_id = options.organization_id
    }

    if (options.domain_ids?.length) {
        contract.filters.push({ field: 'domain.id', operator: 'in', value: options.domain_ids })
    }

    if (contract.order) {
        if (
            contract.order.field === 'send_date' &&
            [
                ReportConfigurationType.SUBSCRIBER,
                ReportConfigurationType.APP_MESSAGE,
                ReportConfigurationType.LIVE_ACTIVITY,
            ].includes(reportConfig.type)
        ) {
            contract.order.field = 'event_date'
        } else if (['send_date', 'event_date'].includes(contract.order.field) && reportConfig.type === 'PUB_NETWORK') {
            contract.order.field = 'report_date'
        } else if (contract.order.field === 'send_date' && reportConfig.type === 'MONTHLY_UNIQUES') {
            contract.order.field = 'report_month'
        }
    }

    if (!!reportConfig.dateRangePresetKey) {
        contract.date_range = getRangeFromPresetKey(reportConfig.dateRangePresetKey)
    } else if (!!reportConfig.dateRange && !!reportConfig.dateRange.since) {
        contract.date_range = reportConfig.dateRange
    } else {
        contract.date_preset = ReportDatePreset.LIFETIME
    }

    if (reportConfig.type === ReportConfigurationType.DELIVERY) {
        contract.entity = ReportEntity.NOTIFICATIONS

        attachContractLevelImplicitBreakdown(contract, reportConfig.level)

        if (reportConfig.level === ReportConfigurationLevel.CAMPAIGN) {
            contract.entity = ReportEntity.CAMPAIGNS
            contract.order!.field = 'event_date'
        }

        attachContractBreakdown(contract, reportConfig.breakdown)

        if (
            contract.organization_id &&
            contract.breakdowns.includes(ReportConfigurationBreakdown.NOTIFICATION.toLowerCase())
        ) {
            attachContractBreakdown(contract, ReportConfigurationBreakdown.NOTIFICATION_GROUP)

            if (!contract.breakdowns.includes(ReportConfigurationBreakdown.DOMAIN.toLowerCase())) {
                contract.breakdowns = contract.breakdowns.filter(
                    (bd) => bd !== ReportConfigurationBreakdown.NOTIFICATION.toLowerCase(),
                )
            }
        }

        if (reportConfig.level === ReportConfigurationLevel.SEGMENT) {
            contract.entity = ReportEntity.SEGMENTS

            attachContractFields(
                contract,
                reportConfig.columns,
                SegmentApiColumns,
                reportConfig,
                type,
                level,
                downloadable,
            )
        } else if (reportConfig.level === ReportConfigurationLevel.CAMPAIGN) {
            attachContractFields(
                contract,
                reportConfig.columns,
                JourneyApiColumns,
                reportConfig,
                type,
                level,
                downloadable,
            )
        } else {
            attachContractFields(
                contract,
                reportConfig.columns,
                NotificationApiColumns,
                reportConfig,
                type,
                level,
                downloadable,
            )
        }

        if (reportConfig.level === 'NOTIFICATION') {
            contract.fields.push('notification.source')
        }

        if (reportConfig.level === 'KEYWORD') {
            attachContractFields(
                contract,
                reportConfig.columns,
                NotificationApiColumns,
                reportConfig,
                type,
                level,
                downloadable,
            )
        }
    } else if (reportConfig.type === ReportConfigurationType.SUBSCRIBER) {
        contract.entity = ReportEntity.PROMPTS

        attachContractLevelImplicitBreakdown(contract, reportConfig.level)
        attachContractBreakdown(contract, reportConfig.breakdown)
        attachContractFields(contract, reportConfig.columns, PromptApiColumns, reportConfig, type, level, downloadable)

        if (reportConfig.level === 'PROMPT') {
            if (
                contract.filters.find((f) => f.field.toUpperCase() === 'TEST_ID') &&
                !contract.fields.includes('test.id')
            ) {
                contract.fields.push('test.id')
            }
        }
    } else if (reportConfig.type === ReportConfigurationType.PUB_NETWORK) {
        contract.entity = ReportEntity.PUB_NETWORK

        attachContractLevelImplicitBreakdown(contract, reportConfig.level)
        attachContractBreakdown(contract, reportConfig.breakdown)
        attachContractFields(
            contract,
            reportConfig.columns,
            PubNetworkApiColumns,
            reportConfig,
            type,
            level,
            downloadable,
        )
    } else if (reportConfig.type === ReportConfigurationType.MONTHLY_UNIQUES) {
        contract.entity = ReportEntity.MONTHLY_UNIQUES

        attachContractLevelImplicitBreakdown(contract, reportConfig.level)
        attachContractBreakdown(contract, reportConfig.breakdown)
        attachContractFields(
            contract,
            reportConfig.columns,
            MonthlyUniquesApiColumns,
            reportConfig,
            type,
            level,
            downloadable,
        )
    } else if (reportConfig.type === ReportConfigurationType.APP_MESSAGE) {
        contract.entity = ReportEntity.APP_MESSAGES

        attachContractLevelImplicitBreakdown(contract, reportConfig.level)
        attachContractBreakdown(contract, reportConfig.breakdown)
        attachContractFields(
            contract,
            reportConfig.columns,
            AppMessageApiColumns,
            reportConfig,
            type,
            level,
            downloadable,
        )
    } else if (reportConfig.type === ReportConfigurationType.LIVE_ACTIVITY) {
        contract.entity = ReportEntity.LIVE_ACTIVITIES

        attachContractLevelImplicitBreakdown(contract, reportConfig.level)
        attachContractBreakdown(contract, reportConfig.breakdown)
        attachContractFields(
            contract,
            reportConfig.columns,
            LiveActivityApiColumns,
            reportConfig,
            type,
            level,
            downloadable,
        )
    }

    if (!!globalTable.sortBy) {
        if (!(globalTable.sortBy in NotificationApiOrderBlacklist)) {
            const sortField = globalTable.sortBy

            if (contract.fields.indexOf(sortField) !== -1) {
                contract.order!.field = sortField

                if (!!globalTable.sortDirection) {
                    contract.order!.direction = globalTable.sortDirection
                }
            }
        }
    }

    // if increment is lifetime and order for date is irrelevant
    if (reportConfig.dateIncrement === ReportDateIncrement.LIFETIME) {
        if (
            contract.order!.field === 'send_date' ||
            contract.order!.field === 'event_date' ||
            contract.order!.field === 'report_date' ||
            contract.order!.field === 'report_month'
        ) {
            delete contract.order
        }
    }

    return contract
}

function attachContractLevelImplicitBreakdown(contract: InsightsReportContract, level: ReportConfigurationLevel): void {
    contract.breakdowns = [level.toLowerCase()]

    if (contract.organization_id) {
        if (!contract.breakdowns.includes(ReportConfigurationBreakdown.ORGANIZATION.toLowerCase())) {
            contract.breakdowns.push(ReportConfigurationBreakdown.ORGANIZATION.toLowerCase())
        }
    }
}

function attachContractBreakdown(
    contract: InsightsReportContract,
    breakdown: ReportConfigurationBreakdown,
    addtBreakdowns: string[] = [],
): void {
    if (breakdown !== 'NONE') {
        let breakdowns = Array.isArray(breakdown) ? breakdown : breakdown.split(',')
        breakdowns = breakdowns.map((bd: string) => bd.trim().toLowerCase())

        contract.breakdowns = [...contract.breakdowns, ...breakdowns].map((bd) => bd.toLowerCase())
    }

    if (addtBreakdowns.length > 0) {
        contract.breakdowns = Array.from(
            new Set([...contract.breakdowns, ...addtBreakdowns].map((bd) => bd.toLowerCase())),
        )
    }
}

function attachContractFields(
    contract: InsightsReportContract,
    columns: string[],
    columnMap: Record<string, string>,
    reportConfig: IReportConfig,
    type: ReportConfigurationType,
    level: ReportConfigurationLevel,
    downloadable?: boolean,
): void {
    const unorderedFields = [...contract.fields, ...columns.map((col: string) => columnMap[col])]

    // reorder fields by column defs
    const orderedFields: string[] = []
    const fieldOptionsKey = `${reportConfig.type}_${reportConfig.level}`
    const fieldOptions = COLUMN_DROPDOWN_OPTIONS[fieldOptionsKey]

    const levelExplicitFields: string[] = []
    switch (reportConfig.level) {
        case ReportConfigurationLevel.NOTIFICATION:
            if (
                !contract.organization_id &&
                contract.breakdowns.includes(ReportConfigurationBreakdown.NOTIFICATION.toLowerCase())
            ) {
                levelExplicitFields.push('notification.id')
            }
            if (contract.organization_id) {
                levelExplicitFields.push('notification_group.id')

                if (contract.breakdowns.includes(ReportConfigurationBreakdown.DOMAIN.toLowerCase())) {
                    levelExplicitFields.push('notification.id')
                }
            }
            break
        case ReportConfigurationLevel.CAMPAIGN:
            levelExplicitFields.push('campaign.id')
            break
        case ReportConfigurationLevel.PROMPT:
            levelExplicitFields.push('prompt.id')
            levelExplicitFields.push('group.id')
            break
        case ReportConfigurationLevel.ORGANIZATION:
            levelExplicitFields.push('organization.id')
            break
        case ReportConfigurationLevel.DOMAIN:
            levelExplicitFields.push('domain.id')
            break
        case ReportConfigurationLevel.TEST:
            levelExplicitFields.push('test.id')
            break
        case ReportConfigurationLevel.SEGMENT:
            levelExplicitFields.push('segment.id')
            break
        case ReportConfigurationLevel.KEYWORD:
            levelExplicitFields.push('keyword')
            break
        case ReportConfigurationLevel.APP_MESSAGE:
            levelExplicitFields.push('app_message.id')
            break
        case ReportConfigurationLevel.ACTIVITY:
            levelExplicitFields.push('activity.id')
            break
    }

    const breakdownExplicitFields = reportConfig
        .breakdown!.split(',')
        .filter((bd) => bd !== 'NONE')
        .map((bd) => bd.toLowerCase())

    if (fieldOptions) {
        const dims: ColumnDropdownOption[] =
            (fieldOptions.find((opts: any) => opts.label.toUpperCase() === 'DIMENSIONS') || ({} as any)).options || []
        const measures: ColumnDropdownOption[] =
            (fieldOptions.find((opts: any) => opts.label.toUpperCase() === 'MEASURES') || ({} as any)).options || []

        const unnestedOptions = [...dims, ...measures]

        unnestedOptions.forEach((opt) => {
            const optKey = columnMap[opt.value]

            if (unorderedFields.indexOf(optKey) !== -1) {
                orderedFields.push(optKey)
            }
        })
    }

    const fields = orderedFields.length > 0 ? orderedFields : unorderedFields

    const scheduledTimeIndex = fields.indexOf('notification.scheduled_time')
    if (scheduledTimeIndex !== -1) {
        fields.splice(scheduledTimeIndex + 1, 0, 'notification.scheduled_time_zone')
    }

    let addtRequiredFields: string[] = []
    const columnConfigs = COLUMN_CONFIGS_BY_TYPE_MAP[type]
    if (!downloadable) {
        fields.forEach((field) => {
            const colKey = field.toUpperCase()
            const config = columnConfigs[colKey]
            if (Array.isArray(config)) {
                config.forEach((conf) => {
                    if (Array.isArray(conf.requiredColumnKeys) && conf.requiredColumnKeys.length) {
                        addtRequiredFields = addtRequiredFields.concat(
                            conf.requiredColumnKeys.filter((key) => !fields.includes(key)),
                        )
                    }
                })
            } else {
                if (Array.isArray(config?.requiredColumnKeys) && config.requiredColumnKeys.length) {
                    addtRequiredFields = addtRequiredFields.concat(
                        config.requiredColumnKeys.filter((key) => !fields.includes(key)),
                    )
                }
            }
        })

        if ([ReportConfigurationLevel.NOTIFICATION, ReportConfigurationLevel.PROMPT].includes(reportConfig.level)) {
            if (!fields.includes('test.id') && contract.filters.find((filter) => filter.field === 'TEST_ID')) {
                addtRequiredFields.push('test.id')
            }
        }

        contract.addtRequiredFields = addtRequiredFields
    }

    contract.fields = orderRequestedFields(
        Array.from(new Set([...levelExplicitFields, ...breakdownExplicitFields, ...addtRequiredFields, ...fields])),
        type,
        level,
    )
}

function orderRequestedFields(
    requestedFields: any[],
    type: ReportConfigurationType,
    level: ReportConfigurationLevel,
): string[] {
    const fields: string[] = []

    const columnDefKey = `${type}_${level}`
    const columnOrder: string[] = COLUMN_TABLE_ORDER[columnDefKey] || []

    requestedFields = requestedFields.filter((col) => !!col).map((col) => col.toUpperCase())

    columnOrder.forEach((col) => {
        if (requestedFields.indexOf(col) !== -1) {
            fields.push(col.toLowerCase())
        }
    })

    return fields
}

function getRangeFromPresetKey(presetKey: string): { since: string; through: string } {
    const rangeKeys = Object.keys(TIME_RANGE_OPTIONS)
    const timeRangeKey = rangeKeys.find((key) => TIME_RANGE_OPTIONS[key]()!.includes(presetKey))
    const range = TIME_RANGE_OPTIONS[timeRangeKey!]()!

    return {
        since: range[0]?.format('YYYY-MM-DD') ?? undefined!,
        through: range[1]?.format('YYYY-MM-DD') ?? undefined!,
    }
}
